import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Slider,
  Select,
  TextField,
  MenuItem,
  Button,
  FormControl,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

interface JobCategoryCardProps {
  category: {
    name: string;
    values: { name: string; difficultyLevel: string }[];
    requiredWeight: number;
    insights: {
      insight: string;
      potentialRisk: string;
      suggestions: string[];
    };
  };
  showWeightBox: boolean;
  showChatBox: boolean;
  updateCategory: (
    name: string,
    newWeight: number,
    newValues: { name: string; difficultyLevel: string }[]
  ) => void;
}

const JobCategoryCard: React.FC<JobCategoryCardProps> = ({
  category,
  updateCategory,
  showChatBox,
  showWeightBox,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [weight, setWeight] = useState(category.requiredWeight);
  const [skills, setSkills] = useState(
    category.values.map((v) => ({ skill: v.name, level: v.difficultyLevel }))
  );

  const handleExpandClick = () => setExpanded(!expanded);

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    const updatedWeight = Array.isArray(newValue) ? newValue[0] : newValue;
    setWeight(updatedWeight);
    updateCategory(
      category.name,
      updatedWeight,
      skills.map((s) => ({
        name: s.skill,
        difficultyLevel: s.level,
      }))
    );
  };

  const handleSkillChange = (
    index: number,
    field: "skill" | "level",
    value: string
  ) => {
    const updatedSkills = skills.map((s, i) =>
      i === index ? { ...s, [field]: value } : s
    );
    setSkills(updatedSkills);
    updateCategory(
      category.name,
      weight,
      updatedSkills.map((s) => ({
        name: s.skill,
        difficultyLevel: s.level,
      }))
    );
  };

  const handleAddSkill = () => {
    const updated = [...skills, { skill: "", level: "" }];
    setSkills(updated);
    updateCategory(
      category.name,
      weight,
      updated.map((s) => ({
        name: s.skill,
        difficultyLevel: s.level,
      }))
    );
  };

  return (
    <Paper
      sx={{
        mb: 2,
        p: { xs: 1, sm: 2 },
        border: "1px solid #D9DDE3",
        borderRadius: "8px",
        width: "99.9%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 1,
          minHeight: { sm: 60 },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", sm: showWeightBox ? "40%" : "15%" },
            order: { xs: 1, sm: 0 },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: { xs: "12px", sm: "13px" },
            }}
          >
            {category.name} required
          </Typography>
          <Typography fontSize={{ xs: "11px", sm: "12px" }} color="#656E81">
            Must-have criteria for this role.
          </Typography>

          {showWeightBox && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mt: 1,
              }}
            >
              {category.values.map((skill) => (
                <Chip
                  key={skill.name}
                  label={skill.name}
                  sx={{
                    backgroundColor: "#5051F91A",
                    color: "#344054",
                    borderRadius: "6px",
                    fontSize: "12px",
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {!showWeightBox && (
          <Box
            sx={{
              display: { xs: "none", sm: "block" },
              width: "30%",
              borderLeft: 1,
              borderRight: 1,
              borderColor: "#f0f0f0",
              px: 3,
            }}
          >
            <Typography sx={{ mb: 2, color: "#000", fontSize: "12px" }}>
              Criteria
            </Typography>
            <Box
              sx={{ display: "flex", flexWrap: "wrap", gap: 1, minHeight: 80 }}
            >
              {category.values.slice(0, 2).map((skill) => (
                <Chip
                  key={skill.name}
                  label={skill.name}
                  sx={{
                    backgroundColor: "#5051F91A",
                    color: "#344054",
                    borderRadius: "6px",
                    fontSize: "12px",
                  }}
                />
              ))}

              {category.values.length > 2 && (
                <Chip
                  label={`+${category.values.length - 2} more`}
                  sx={{
                    backgroundColor: "#E0E7FF",
                    color: "#1E3A8A",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            width: { xs: "100%", sm: "40%" },
            order: { xs: 3, sm: 0 },
            mt: { xs: 1, sm: 0 },
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: { xs: "11px", sm: "12px" },
              mb: 0.5,
            }}
          >
            Criteria Weight
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: "120px",
                display: "flex",
                alignItems: "center",
                height: "8px",
                backgroundColor: "#E5E7EB",
                borderRadius: "4px",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  width: `${weight}%`,
                  height: "100%",
                  backgroundColor: "#4F46E5",
                  borderRadius: "4px",
                  transition: "width 0.3s ease-in-out",
                }}
              />
            </Box>
            <Typography
              sx={{ fontSize: { xs: "11px", sm: "12px" }, fontWeight: 600 }}
            >
              {weight}%
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={handleExpandClick}
          sx={{
            p: 1,
            order: { xs: 2, sm: 0 },
            alignSelf: { xs: "flex-end", sm: "center" },
          }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {expanded && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: showChatBox || showWeightBox ? "column" : "row",
            },
            gap: 2,
            bgcolor: "#325CD61A",
            p: { xs: 1, sm: 2 },
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              width: {
                xs: "100%",
                sm: showChatBox || showWeightBox ? "90%" : "50%",
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: "13px", sm: "14px" },
                mb: 2,
              }}
            >
              {category.name} Criteria
            </Typography>

            {skills.map((entry, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  mb: 1,
                }}
              >
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <TextField
                    size="small"
                    value={entry.skill}
                    onChange={(e) =>
                      handleSkillChange(index, "skill", e.target.value)
                    }
                    sx={{
                      bgcolor: "white",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#E5E7EB",
                      },
                      borderRadius: "8px",
                    }}
                  />
                </FormControl>

                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <Select
                    value={entry.level}
                    onChange={(e) =>
                      handleSkillChange(index, "level", e.target.value)
                    }
                    displayEmpty
                    sx={{
                      bgcolor: "white",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#E5E7EB",
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select Level
                    </MenuItem>
                    <MenuItem value={"Intermediate"}>Intermediate</MenuItem>
                    <MenuItem value={"Advanced"}>Advanced</MenuItem>
                    <MenuItem value={"Expert"}>Expert</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            ))}

            <Button
              startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
              onClick={handleAddSkill}
              variant="text"
              color="primary"
              sx={{
                mt: 1,
                fontSize: { xs: "12px", sm: "13px" },
                bgcolor: "#ffffff",
                padding: "10px 16px",
                textTransform: "none",
                fontWeight: 500,
                color: "#333333",
              }}
            >
              Add more required skills
            </Button>
          </Box>

          <Box
            sx={{
              width: {
                xs: "100%",
                sm: showChatBox || showWeightBox ? "90%" : "45%",
              },
              p: { xs: 1, sm: 2 },
              border: "1px solid #325CD6",
              borderRadius: "8px",
              bgcolor: "#F9FAFB",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "13px", sm: "14px" },
                fontWeight: 600,
                mb: 2,
              }}
            >
              Criteria Weight
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <Slider
                value={weight}
                onChange={handleSliderChange}
                step={5}
                min={0}
                max={100}
                sx={{
                  width: { xs: "100%", sm: "80%" },
                  color: "#4F46E5",
                }}
              />
              <Typography
                sx={{ fontSize: { xs: "13px", sm: "14px" }, fontWeight: 600 }}
              >
                {weight}%
              </Typography>
            </Box>

            <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
              <InfoOutlinedIcon sx={{ color: "#325CD6", fontSize: 20 }} />
              <Box>
                <Typography
                  fontSize={{ xs: "13px", sm: "14px" }}
                  fontWeight={700}
                  color="#051638"
                >
                  Insight:
                </Typography>
                <Typography fontSize={{ xs: "12px", sm: "13px" }}>
                  {category.insights.insight}
                </Typography>
              </Box>
            </Box>

            <Typography
              sx={{
                fontSize: { xs: "12px", sm: "13px" },
                fontWeight: 700,
                mb: 1,
                color: "#051638",
              }}
            >
              🔷 Suggestions:
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {category.insights.suggestions.map((suggestion, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
                >
                  <CheckCircleOutlineIcon
                    sx={{ color: "#10B981", fontSize: 18 }}
                  />
                  <Typography fontSize={{ xs: "12px", sm: "13px" }}>
                    {suggestion}
                  </Typography>
                </Box>
              ))}

              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <WarningAmberIcon sx={{ color: "#F59E0B", fontSize: 18 }} />
                <Typography fontSize={{ xs: "12px", sm: "13px" }}>
                  {category.insights.potentialRisk}
                </Typography>
              </Box>
            </Box>

            {weight > 20 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  color: "#F04438",
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                <Typography
                  fontSize={{ xs: "12px", sm: "14px" }}
                  fontWeight={500}
                >
                  The weight exceeds the limit. Make it ≤ 20% or adjust other
                  criteria.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default JobCategoryCard;
